import { setContext } from '@apollo/client/link/context';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  getAuth,
  onAuthStateChanged,
  signInWithPhoneNumber,
  signOut,
} from 'firebase/auth';

import {
  defaultRedirectRoute,
  EMAIL_CHECK_URL,
  mobRefreshTokenKey,
  OTP_CHECK_URL,
  redirecturlQueryParam,
} from '../../app/constants/constants';
import ENV from '../../app/constants/env';
import { deleteUserAccessToken } from '../../app/main/mainSlice';
import { RootState } from '../../app/store/store';
import { getFbAuth } from '../../firebase/config';
import generateRecaptcha from '../../firebase/utils';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage,
} from '../../utils/localStorageUtils';
import { logEvents } from '../../utils/logging';
import {
  authFulfilled,
  authRejected,
  logoutUser,
  setDataAuth,
  setFireBaseAuth,
  setIsAuthLoading,
} from './auth.slice';
import { FireBaseAndCreateUserData } from './auth.types';

const getDataAuthToken = ({
  fireBaseJWTMob,
  fireBaseUserId,
  phone,
  userName,
}: FireBaseAndCreateUserData) =>
  axios.post(`${ENV.REGISTRATION_GCP_ENDPOINT}/phone-auth-check`, {
    fireBaseJWTMob,
    fireBaseUserId,
    phone,
    userName,
  });

const getEmailCheck = ({
  userName,
  email,
  fireBaseUserId,
  phone,
  fireBaseJWTMob,
}: {
  userName: string;
  email: string;
  fireBaseUserId: string;
  phone: string;
  fireBaseJWTMob: string;
}) =>
  axios.post(`${ENV.REGISTRATION_GCP_ENDPOINT}/email-check`, {
    email,
    fireBaseJWTMob,
    fireBaseUserId,
    phone,
    userName,
  });

export const fireBaseAuthCall = createAsyncThunk(
  'auth/firebaseAuth',
  async (otp: string) => {
    const { confirmationResult } = window;
    const result = await confirmationResult.confirm(otp);

    const { user } = result;
    const userFbId = user.uid;

    const fbTokenCall = await user.getIdTokenResult();
    const fbToken = fbTokenCall.token;

    return { fbToken, userFbId };
  }
);

const getUserMobTokenResponse = (res: any) => {
  // fix
  const {
    data: { refreshTokenForMobile },
  } = res;
  return refreshTokenForMobile;
};

const getUserHasuraId = (res: any) => {
  const {
    data: { userHasuraID },
  } = res;
  return userHasuraID;
};

const setApolloAuthContext = (token: string | null) => {
  const bearerToken = token ?? process.env.REACT_APP_ANONYMOUS_API_KEY;
  setContext((_, { headers }) => ({
    headers: {
      ...headers,
      // "x-hasura-admin-secret": process.env.REACT_APP_STAGING_DB_PASSWORD,
      authorization: `Bearer ${bearerToken}`,
    },
  }));
};

export const dataAuthCall = createAsyncThunk(
  'auth/dataAuth',
  async ({
    fbToken,
    userFbId,
    userMobToken,
    userHasuraID,
    phone,
    userName,
  }: {
    fbToken: string;
    userFbId: string;
    userMobToken: string | null;
    userHasuraID: string | null;
    phone: string;
    userName?: string;
  }) => {
    if (userMobToken && userHasuraID) {
      return {
        refreshTokenForMobile: userMobToken,
        userHasuraID,
        isExistingUser: undefined,
      }; // Or a sensible default/previous value for isExistingUser
    }

    try {
      const responseDataAuthToken = await getDataAuthToken({
        fireBaseJWTMob: fbToken,
        fireBaseUserId: userFbId,
        phone,
        userName,
      });
      const {
        refreshTokenForMobile,
        userHasuraID: resUserHasuraID,
        isExistingUser,
      } = responseDataAuthToken.data;
      return {
        refreshTokenForMobile,
        userHasuraID: resUserHasuraID,
        isExistingUser,
      };
    } catch (err: any) {
      console.error(err);
      throw new Error(err.response?.status || 'Unknown error');
    }
  }
);

export const authCall =
  ({
    history,
    redirectToPath,
    otp,
    phone,
    cookClubLog,
  }: {
    history: any;
    redirectToPath: string | null;
    otp: string;
    phone: string;
    cookClubLog: any;
  }) =>
  async (dispatch: any, getState: any) => {
    const state: RootState = getState();
    const localJwtToken = state.main.localMobRefreshToken;
    const userHasuraId = state.auth.userHasuraID;

    const fbData = await dispatch(fireBaseAuthCall(otp));
    const fbToken = fbData?.payload?.fbToken;
    const userFbId = fbData?.payload?.userFbId;

    if (!fbToken || !userFbId) {
      cookClubLog(
        `Error! Please try again.`,
        '',
        {},
        { ui: { type: 'error' }, browserConsole: true }
      );
      dispatch(authRejected());
      return;
    }

    const dbAuthData = await dispatch(
      dataAuthCall({
        fbToken,
        userFbId,
        userMobToken: localJwtToken,
        userHasuraID: userHasuraId,
        phone,
      })
    );

    if (
      dbAuthData?.error &&
      dbAuthData.error.message !== '404' &&
      dbAuthData.error.message !== '200'
    ) {
      // TODO handle error in better way
      cookClubLog(
        `Error! Please try again.`,
        '',
        {},
        { ui: { type: 'error' }, browserConsole: true }
      );
      history.push(`/sign-in?${redirecturlQueryParam}=${redirectToPath}`);
      dispatch(authRejected());
      return;
    }
    if (
      !!dbAuthData.payload?.refreshTokenForMobile &&
      !!dbAuthData.payload?.userHasuraID
    ) {
      const { refreshTokenForMobile, userHasuraID, isExistingUser } =
        dbAuthData.payload;
      setLocalStorage(mobRefreshTokenKey, refreshTokenForMobile);
      dispatch(
        authFulfilled({
          userMobToken: refreshTokenForMobile,
          userHasuraID,
          isExistingUser,
        })
      );

      logEvents('null', 'APP_USER_LOGGED_IN', {
        userId: userHasuraID,
      });

      if (redirectToPath) {
        history.push(redirectToPath);
      } else {
        history.push(defaultRedirectRoute);
      }
    } else {
      dispatch(authRejected());
      history.push(
        `${EMAIL_CHECK_URL}?${redirecturlQueryParam}=${redirectToPath}`
      );
    }
  };

export const emailAuthCheckCall = createAsyncThunk(
  'auth/emailCheck',
  async ({
    userName,
    email,
    phone,
    fireBaseJWTMob,
    fireBaseUserId,
    cookClubLog,
  }: {
    userName: string;
    email: string;
    phone: string | null;
    fireBaseJWTMob: string | null;
    fireBaseUserId: string | null;
    cookClubLog: any;
  }) => {
    if (!!phone && !!fireBaseJWTMob && !!fireBaseUserId && !!userName) {
      const emailCheckRes = await getEmailCheck({
        userName,
        email,
        fireBaseUserId,
        phone,
        fireBaseJWTMob,
      });

      if (
        !!emailCheckRes?.data?.refreshTokenForMobile &&
        !!emailCheckRes?.data?.userHasuraID
      ) {
        setLocalStorage(
          mobRefreshTokenKey,
          emailCheckRes.data.refreshTokenForMobile
        );
        return {
          userMobToken: emailCheckRes.data.refreshTokenForMobile,
          userHasuraID: emailCheckRes.data.userHasuraID,
          isExistingUser: emailCheckRes.data.isExistingUser,
        };
      }

      cookClubLog(
        `Error verifying/creating user: Please try again.`,
        '',
        {},
        { ui: { type: 'error' }, browserConsole: true }
      );
      throw new Error('User verification/creation failed');
    }

    cookClubLog(
      `Session Timed out: Please login again.`,
      '',
      {},
      { ui: { type: 'error' }, browserConsole: true }
    );
    throw new Error('Email Name Phone and firebase token are required');
  }
);

/**
 * Redux async thunk that handles phone number submission for Firebase authentication.
 *
 * This thunk performs the following operations:
 * 1. Sets up or reuses a reCAPTCHA verifier
 * 2. Initiates the phone authentication process with Firebase
 * 3. Redirects to the OTP verification page on success
 * 4. Handles various error scenarios with appropriate user messages
 *
 * @param {Object} params - The parameters for phone number submission
 * @param {string} params.phoneNumber - The user's phone number in E.164 format
 * @param {string|null} [params.redirecturl] - Optional URL to redirect after successful verification
 * @param {any} [params.history] - React Router history object for navigation
 * @param {Function} params.cookClubLog - Logging function for tracking events and errors
 *
 * @returns {Promise<{phoneNumber: string, redirecturl: string}>} Object containing the phone number and redirect URL
 *
 * @throws {Error} Throws an error if reCAPTCHA verification fails, phone number is invalid, or other authentication issues occur
 */
export const phoneNumberSubmit = createAsyncThunk(
  'auth/fireBasePhoneNumberSubmit',
  async ({
    phoneNumber,
    redirecturl,
    history,
    cookClubLog,
  }: {
    phoneNumber: string;
    redirecturl?: string | null;
    history?: any;
    cookClubLog: any;
  }) => {
    const auth = getAuth();
    let { recaptchaVerifier } = window;

    if (!recaptchaVerifier || recaptchaVerifier.destroyed) {
      generateRecaptcha();
      // Re-fetch the verifier after generation
      recaptchaVerifier = window.recaptchaVerifier;
    }

    // Ensure appVerifier is available after attempting to generate it
    if (!recaptchaVerifier) {
      cookClubLog(
        `reCAPTCHA verifier could not be initialized. Please refresh and try again.`,
        'recaptcha_initialization_failed',
        {},
        { ui: { type: 'error' }, browserConsole: true }
      );
      throw new Error('reCAPTCHA verifier initialization failed.');
    }

    const appVerifier = recaptchaVerifier;

    try {
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        appVerifier
      );
      window.confirmationResult = confirmationResult;

      if (history) {
        const targetUrl = redirecturl
          ? `${OTP_CHECK_URL}?${redirecturlQueryParam}=${redirecturl}`
          : OTP_CHECK_URL;
        history.push(targetUrl);
      }

      return {
        phoneNumber,
        redirecturl: redirecturl ?? defaultRedirectRoute,
      };
    } catch (err: any) {
      console.error('Error during phone number submission:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      console.error('Error details:', JSON.stringify(err, null, 2));
      console.error('Error during phone number submission:', err);
      // Clear reCAPTCHA verifier if it exists
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }

      let userErrorMessage = `Error! Please try againdasdasdasd.`;
      if (
        err.code === 'auth/captcha-check-failed' ||
        err.message?.includes('reCAPTCHA')
      ) {
        userErrorMessage = `reCAPTCHA verification failed. Please try again.`;
      } else if (err.code === 'auth/invalid-phone-number') {
        userErrorMessage = `Invalid phone number. Please check and try again.`;
      }

      cookClubLog(
        userErrorMessage,
        err.code || 'phone_submit_error',
        { error: String(err) },
        { ui: { type: 'error' }, browserConsole: true }
      );
      throw new Error(String(err.message || err));
    }
  }
);

export const generateRefreshToken = ({
  userHID,
}: {
  deviceId: string;
  oldJwt: string;
  userHID: string;
}) =>
  axios.post(`${ENV.REGISTRATION_GCP_ENDPOINT}/generate-mob-refresh-token`, {
    userHID,
  });

const getFbUser = () =>
  new Promise((resolve) => {
    // console.log({ step: 'getFbUser' });
    onAuthStateChanged(getFbAuth, (fbUser: any) => {
      // console.log({ step: 'onAuthStateChanged', fbUser });
      resolve(fbUser);
    });
  });

export const fireBaseAuthCheck = async () => {
  const fbUser = await getFbUser();
  // console.log({ step: 'getFbUser', fbUser });
  return !!fbUser;

  // const fbToken = await getFbAuth.currentUser?.getIdToken();
  // onAuthStateChanged(getFbAuth, (fbUser: any) => {
  //   return !!fbUser;
  // })
};

export const dataAuthCheck = () =>
  !!getLocalStorage(mobRefreshTokenKey) || !!getLocalStorage('userMobToken');

export const authInit = () => async (dispatch: any) => {
  dispatch(setIsAuthLoading(true));
  const firebaseAuth = await fireBaseAuthCheck();
  dispatch(setFireBaseAuth(firebaseAuth));
  const dataAuth = dataAuthCheck();
  dispatch(setDataAuth(dataAuth));
  if (!(firebaseAuth && dataAuth)) dispatch(setIsAuthLoading(false));
};

export const logout = (isTokenExp?: boolean) => async (dispatch: any) => {
  await signOut(getFbAuth);
  if (!isTokenExp) window?.recaptchaVerifier?.clear();
  deleteLocalStorage(mobRefreshTokenKey);
  dispatch(logoutUser());
  dispatch(deleteUserAccessToken());
  setApolloAuthContext(null);
  window.webkit?.messageHandlers?.orgHandler?.postMessage('');

  if (window?.NativeCookie?.setCookie) {
    window.NativeCookie.setCookie('userDomain=; Path=/; SameSite=Lax');
  }
};
